import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Wrench, ShieldCheck, ArrowRight, Phone, ExternalLink, Check, Snowflake } from "lucide-react";

const SINGLE_ZONE = [
  {
    name: "Senville LETO 9,000 BTU",
    model: "SENL/09CD",
    btu: "9,000 BTU",
    coverage: "Up to ~400 sq ft",
    price: "$699.99",
    badge: "Best Value",
    url: "https://senville.com/9000-btu-mini-split-air-conditioner-senl-09cd/",
    img: "https://cdn11.bigcommerce.com/s-2tb0afw6nn/images/stencil/500x659/products/112/2282/senville-9000-btu-mini-split-air-conditioner-heat-pump-senl09cd110v__84216.1709835977.jpg?c=2",
  },
  {
    name: "Senville LETO 12,000 BTU",
    model: "SENL/12CD",
    btu: "12,000 BTU",
    coverage: "Up to ~550 sq ft",
    price: "$799.99",
    badge: "Most Popular",
    url: "https://senville.com/12000-btu-mini-split-air-conditioner-senl-12cd/",
    img: "https://cdn11.bigcommerce.com/s-2tb0afw6nn/images/stencil/500x659/products/113/2755/senville-12000-btu-mini-split-air-conditioner-heat-pump-senl12cd110v__53174.1709836713.jpg?c=2",
  },
  {
    name: "Senville LETO 18,000 BTU",
    model: "SENL/18CD",
    btu: "18,000 BTU",
    coverage: "Up to ~800 sq ft",
    price: "$999.99",
    badge: null,
    url: "https://senville.com/18000-btu-mini-split-air-conditioner-heat-pump-senl-18cd/",
    img: "https://cdn11.bigcommerce.com/s-2tb0afw6nn/images/stencil/500x659/products/114/2501/senville-18000-btu-mini-split-air-conditioner-heat-pump-senl18cd__81134.1767709967.jpg?c=2",
  },
  {
    name: "Senville LETO 24,000 BTU",
    model: "SENL/24CD",
    btu: "24,000 BTU",
    coverage: "Up to ~1,100 sq ft",
    price: "$1,249.99",
    badge: null,
    url: "https://senville.com/24000-btu-mini-split-air-conditioner-senl-24cd/",
    img: "https://cdn11.bigcommerce.com/s-2tb0afw6nn/images/stencil/500x659/products/115/2836/senville-24000-btu-mini-split-air-conditioner-heat-pump-senl24cd__91884.1709836885.jpg?c=2",
  },
  {
    name: "Senville LETO 36,000 BTU",
    model: "SENL/36CD",
    btu: "36,000 BTU",
    coverage: "Up to ~1,600 sq ft",
    price: "$2,099.99",
    badge: null,
    url: "https://senville.com/36000-btu-mini-split-air-conditioner-senl-36cd/",
    img: "https://cdn11.bigcommerce.com/s-2tb0afw6nn/images/stencil/500x659/products/250/3008/senville-36000-btu-mini-split-air-conditioner-heat-pump-senl36cd__21763.1709837153.jpg?c=2",
  },
  {
    name: "Senville AURA 24,000 BTU",
    model: "SENA/24HF",
    btu: "24,000 BTU",
    coverage: "Hyper heat • cold-climate",
    price: "$1,799.99",
    badge: "Premium",
    url: "https://senville.com/24000-btu-mini-split-air-conditioner-sena-24hf/",
    img: "https://cdn11.bigcommerce.com/s-2tb0afw6nn/images/stencil/500x659/products/119/2347/senville-24000-btu-mini-split-air-conditioner-heat-pump-sena24hf__39288.1709836124.jpg?c=2",
  },
];

const STEPS = [
  { icon: ShoppingCart, title: "1. Pick & Purchase", desc: "Choose your system below and buy it directly from Senville at online prices." },
  { icon: Wrench, title: "2. We Install It", desc: "Our licensed technicians (CLSB# 1054485) professionally install your new system." },
  { icon: ShieldCheck, title: "3. Warranty Secured", desc: "Professional installation keeps your full Senville manufacturer warranty intact." },
];

export default function ProductsPage() {
  useEffect(() => {
    document.title = "Shop HVAC Systems | Virgil's Heating & Air Conditioning";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Buy your own Senville mini split or central HVAC system and let Virgil's install it — keeping your full manufacturer warranty. Serving the Greater Sacramento Area.");
    }
  }, []);

  return (
    <div className="pt-36 min-h-screen bg-[#050505]" data-testid="products-page">
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-[#0a0a12] to-[#0a0a0a]" data-testid="products-hero">
        <div className="container-custom">
          <p className="font-mono text-[#5b93ff] text-sm uppercase tracking-wider mb-2 font-semibold">Shop Systems</p>
          <h1 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl gradient-text mb-4">
            BUY YOUR SYSTEM,<br />WE INSTALL IT
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl">
            Purchase your own Senville mini split or central HVAC system at online prices, and our 
            licensed team installs it for you — so you save money <span className="text-white font-semibold">and</span> keep your 
            full manufacturer warranty.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-[#0a0a0a] border-y border-white/5" data-testid="how-it-works">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="font-mono text-[#5b93ff] text-sm uppercase tracking-wider mb-2 font-semibold">How It Works</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl gradient-text">SIMPLE &amp; SMART</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {STEPS.map((step, i) => (
              <div key={i} className="glass-panel p-8 text-center" data-testid={`step-${i}`}>
                <div className="icon-container w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-2xl">
                  <step.icon className="w-10 h-10 text-[#5b93ff]" />
                </div>
                <h3 className="font-heading font-bold text-xl text-white mb-2">{step.title}</h3>
                <p className="text-gray-400">{step.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 glass-panel p-6 flex items-start gap-3 max-w-3xl mx-auto" data-testid="warranty-note">
            <ShieldCheck className="w-6 h-6 text-[#5b93ff] shrink-0 mt-0.5" />
            <p className="text-gray-300 text-sm">
              <span className="text-white font-semibold">Why professional installation matters:</span> Manufacturer
              warranties typically require installation by a licensed HVAC professional. When Virgil's installs
              your Senville system, your equipment warranty stays fully valid.
            </p>
          </div>
        </div>
      </section>

      {/* Single-Zone Mini Splits */}
      <section className="section-padding bg-[#050505]" data-testid="mini-splits-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="font-mono text-[#5b93ff] text-sm uppercase tracking-wider mb-2 font-semibold">Single-Zone Mini Splits</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl gradient-text mb-4">DUCTLESS HEAT PUMP SYSTEMS</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Energy-efficient heating &amp; cooling for a single room or open space. Prices shown are set by Senville.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SINGLE_ZONE.map((p, i) => (
              <div
                key={i}
                className="bg-[#101014] border border-white/10 rounded-2xl overflow-hidden flex flex-col hover:border-[#1E6BFF]/60 hover:shadow-[0_0_30px_rgba(30,107,255,0.2)] transition-all duration-300"
                data-testid={`product-card-${i}`}
              >
                <div className="relative bg-gradient-to-b from-white to-gray-100 p-4">
                  {p.badge && (
                    <span className="absolute top-3 left-3 z-10 font-mono text-[10px] uppercase tracking-wider bg-[#1E6BFF] text-white px-2.5 py-1 rounded-full shadow-[0_0_12px_rgba(30,107,255,0.6)]">
                      {p.badge}
                    </span>
                  )}
                  <img src={p.img} alt={p.name} loading="lazy" className="w-full h-52 object-contain" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-heading font-bold text-lg text-white">{p.name}</h3>
                  <p className="font-mono text-xs text-gray-500 mb-3">Model {p.model}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="inline-flex items-center gap-1 text-xs text-[#5b93ff] bg-[#1E6BFF]/10 px-2.5 py-1 rounded-full">
                      <Snowflake className="w-3 h-3" /> {p.btu}
                    </span>
                    <span className="text-xs text-gray-400 bg-white/5 px-2.5 py-1 rounded-full">{p.coverage}</span>
                  </div>
                  <ul className="space-y-1.5 mb-5">
                    <li className="text-gray-400 text-xs flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#5b93ff]" /> Heating &amp; cooling heat pump</li>
                    <li className="text-gray-400 text-xs flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#5b93ff]" /> Pro install by Virgil's included*</li>
                  </ul>
                  <div className="mt-auto">
                    <p className="font-heading font-black text-2xl text-white mb-3">{p.price}</p>
                    <div className="flex flex-col gap-2">
                      <a href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`buy-btn-${i}`}>
                        <Button className="btn-primary w-full">
                          Buy on Senville
                          <ExternalLink className="ml-2 w-4 h-4" />
                        </Button>
                      </a>
                      <Link to="/booking" data-testid={`install-btn-${i}`}>
                        <Button variant="outline" className="btn-outline w-full">Book Installation</Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="text-gray-500 text-xs text-center mt-6 max-w-2xl mx-auto">
            *Installation quoted separately based on your home. Equipment is purchased directly from Senville;
            Virgil's Heating &amp; Air handles professional installation. Prices subject to change.
          </p>

          <div className="text-center mt-8">
            <a href="https://senville.com/mini-split-ac/single-zone/" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="btn-outline" data-testid="view-all-senville-btn">
                View All Single-Zone Systems on Senville
                <ExternalLink className="ml-2 w-4 h-4" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Central / Whole-Home Systems */}
      <section className="section-padding bg-[#0a0a0a] border-y border-white/5" data-testid="central-section">
        <div className="container-custom">
          <div className="glass-panel p-8 md:p-12 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <p className="font-mono text-[#5b93ff] text-sm uppercase tracking-wider mb-2 font-semibold">Whole-Home Comfort</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">CENTRAL &amp; MULTI-ZONE HVAC</h2>
              <p className="text-gray-300 mb-6">
                Need to cool the whole house? Senville's multi-zone and concealed-duct systems let one outdoor
                unit serve multiple rooms — a true central-style solution. Pick the right system and we'll handle
                the full professional installation.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="text-gray-300 flex items-center gap-2"><Check className="w-5 h-5 text-[#5b93ff]" /> Multi-room &amp; ducted options</li>
                <li className="text-gray-300 flex items-center gap-2"><Check className="w-5 h-5 text-[#5b93ff]" /> Manufacturer warranty preserved</li>
                <li className="text-gray-300 flex items-center gap-2"><Check className="w-5 h-5 text-[#5b93ff]" /> Free installation consultation</li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="https://senville.com/mini-split-ac/concealed-duct/" target="_blank" rel="noopener noreferrer" data-testid="central-shop-btn">
                  <Button className="btn-primary w-full sm:w-auto">
                    Shop Central Systems
                    <ExternalLink className="ml-2 w-4 h-4" />
                  </Button>
                </a>
                <Link to="/contact" data-testid="central-quote-btn">
                  <Button variant="outline" className="btn-outline w-full sm:w-auto">Request a Quote</Button>
                </Link>
              </div>
            </div>
            <div className="flex justify-center">
              <Snowflake className="w-40 h-40 text-[#1E6BFF]/40" strokeWidth={1} />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#050505] relative overflow-hidden" data-testid="products-cta">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#1E6BFF]/15 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-[#FF2D1A]/15 rounded-full blur-[120px]"></div>
        </div>
        <div className="container-custom relative z-10 text-center">
          <h2 className="font-heading font-black text-3xl md:text-4xl text-white mb-4 uppercase">
            Not Sure Which System <span className="neon-blue-text">You Need?</span>
          </h2>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            Give us a call and we'll help you size the right system for your space before you buy.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="tel:7073843409">
              <Button className="btn-urgent text-lg px-8 py-6" data-testid="products-call-btn">
                <Phone className="mr-2 w-5 h-5" />
                707-384-3409
              </Button>
            </a>
            <Link to="/booking">
              <Button className="btn-primary text-lg px-8 py-6" data-testid="products-book-btn">
                Book Installation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
